import React, { useState, useEffect } from 'react';
import { getBirdsEyeView } from '../../_services/schedules.service';
import type { BirdsEyeViewData, BirdsEyeViewFilters, ActivitySlot } from '../../_models/schedule.model';
import { Box, CircularProgress, Typography, Paper, Grid } from '@material-ui/core';
import KPICards from './components/analytics/KPICards';
import FiltersPanel from './components/analytics/FiltersPanel';
import RoomUtilizationChart from './components/analytics/RoomUtilizationChart';
import InstructorUtilizationChart from './components/analytics/InstructorUtilizationChart';
import ShiftUtilizationChart from './components/analytics/ShiftUtilizationChart';
import UtilizationHeatmap from './components/analytics/UtilizationHeatmap';
import StatusDistributionChart from './components/analytics/StatusDistributionChart';
import ConflictAnalysisPanel from './components/analytics/ConflictAnalysisPanel';
import './analytics.css';

const AnalyticsTab: React.FC = () => {
  // Helper function to get default date range (current month)
  const getDefaultDateRange = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: endOfMonth.toISOString().split('T')[0],
    };
  };

  const [data, setData] = useState<BirdsEyeViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BirdsEyeViewFilters>(getDefaultDateRange());
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBirdsEyeView(filters);
      if (response.success && response.data) {
        setData(response.data);
      } else {
        setError(response.message || 'Failed to load analytics data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred loading analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<BirdsEyeViewFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const clearFilters = () => {
    setFilters(getDefaultDateRange());
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={60} style={{ color: 'var(--brand-primary)' }} />
        <Typography variant="h6" style={{ marginTop: 16, color: 'var(--text-muted)' }}>
          Loading analytics data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <Typography variant="h5" style={{ color: '#e53e3e', marginBottom: 16 }}>
          Error Loading Analytics
        </Typography>
        <Typography variant="body1" style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
          {error}
        </Typography>
        <button onClick={loadData} className="analytics-retry-btn">
          Retry
        </button>
      </Box>
    );
  }

  if (!data || data.slots.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <Typography variant="h4" style={{ marginBottom: 16, fontSize: '48px' }}>
          📊
        </Typography>
        <Typography variant="h5" style={{ color: 'var(--text-strong)', marginBottom: 8 }}>
          No Data Available
        </Typography>
        <Typography variant="body1" style={{ color: 'var(--text-muted)' }}>
          Try adjusting your date range to see analytics
        </Typography>
      </Box>
    );
  }

  return (
    <div className="analytics-container">
      <Box mb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" style={{ fontWeight: 700, color: 'var(--text-strong)' }}>
              Analytics Dashboard
            </Typography>
            <Typography variant="body2" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
              Comprehensive utilization and performance metrics
            </Typography>
          </Box>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="analytics-toggle-filters-btn"
          >
            {showFilters ? '🔼 Hide Filters' : '🔽 Show Filters'}
          </button>
        </Box>

        {showFilters && (
          <Box mt={3}>
            <FiltersPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
            />
          </Box>
        )}
      </Box>

      {/* KPI Cards */}
      <Box mb={4} mt={2}>
        <KPICards analytics={data.analytics} />
      </Box>

      {/* Status Distribution */}
      <Box mb={4}>
        <Paper elevation={2} style={{ padding: '24px', borderRadius: '12px' }}>
          <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
            Status Distribution
          </Typography>
          <StatusDistributionChart analytics={data.analytics} />
        </Paper>
      </Box>

      {/* Room and Instructor Utilization */}
      <Grid container spacing={3} style={{ marginBottom: 32 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} style={{ padding: '24px', borderRadius: '12px', height: '100%' }}>
            <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
              Room Utilization
            </Typography>
            <RoomUtilizationChart groupedByRoom={data.groupedByRoom} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} style={{ padding: '24px', borderRadius: '12px', height: '100%' }}>
            <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
              Instructor Workload
            </Typography>
            <InstructorUtilizationChart groupedByInstructor={data.groupedByInstructor} />
          </Paper>
        </Grid>
      </Grid>

      {/* Shift Distribution */}
      <Box mb={4}>
        <Paper elevation={2} style={{ padding: '24px', borderRadius: '12px' }}>
          <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
            Shift Distribution
          </Typography>
          <ShiftUtilizationChart groupedByShift={data.groupedByShift} />
        </Paper>
      </Box>

      {/* Time-based Heatmap */}
      <Box mb={4}>
        <Paper elevation={2} style={{ padding: '24px', borderRadius: '12px' }}>
          <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 600, color: 'var(--text-strong)' }}>
            Weekly Activity Heatmap
          </Typography>
          <UtilizationHeatmap slots={data.slots} />
        </Paper>
      </Box>

      {/* Conflict Analysis */}
      <Box mb={4}>
        <ConflictAnalysisPanel slots={data.slots} />
      </Box>
    </div>
  );
};

export default AnalyticsTab;
